import {
  Error as ErrorIcon,
  Information as InformationIcon,
  Success as SuccessIcon,
  Warning as WarningIcon,
} from '@mos-cat/ds-icons';
import * as React from 'react';
import { createComponent, createHook } from 'reakit-system';
import { HTMLAttributes } from 'react';
import { ResponsiveValue, variant } from 'styled-system';
import { As, forwardRefWithAs } from '../as';
import { Box, useBox } from '../Box/Box';
import { cx, ecss } from '../reexports/emotion';
import { Stack } from '../Stack/Stack';
import { StylesOptions } from '../hooks/useStyles';

const variants = variant({
  prop: 'kind',
  variants: {
    error: {
      borderLeftColor: ['error-60', 'gray-20'],
      borderTopColor: ['gray-20', 'error-60'],
      [`& > svg`]: {
        borderRadius: 'round',
        boxShadow: ['none', '0 0 0 2px white, 0 0 0 6px #FFDEDD'],
      },
    },
    warning: {
      borderLeftColor: ['warning-60', 'gray-20'],
      borderTopColor: ['gray-20', 'warning-60'],
      '& > svg': {
        borderRadius: 'round',
        boxShadow: ['none', '0 0 0 2px white, 0 0 0 6px #fff1cc'],
      },
    },
    success: {
      borderLeftColor: ['success-60', 'gray-20'],
      borderTopColor: ['gray-20', 'success-60'],
      '& > svg': {
        borderRadius: 'round',
        boxShadow: ['none', '0 0 0 2px white, 0 0 0 6px #add8c7'],
      },
    },
    info: {
      borderLeftColor: ['blue-60', 'gray-20'],
      borderTopColor: ['gray-20', 'blue-60'],
      '& > svg': {
        borderRadius: 'round',
        boxShadow: ['none', '0 0 0 2px white, 0 0 0 6px #bbcfff'],
      },
    },
  },
});

const useMessage = createHook<MessageOptions, HTMLAttributes<HTMLElement>>({
  name: 'Message',
  keys: ['kind'],
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      styles: {
        position: 'relative',
        maxWidth: 'semi-wide',
        borderWidth: 2,
        borderLeftWidth: [4, 2],
        borderTopWidth: [2, 4],
        borderRadius: 4,
        borderColor: 'gray-20',
        display: 'flex',
        flexDirection: ['column', 'row'],
        alignContent: 'flex-start',
        p: 'sp-16',
        pl: [null, 'sp-12'],
        '> svg': {
          borderRadius: '50%',
        },
        ...options.styles,
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        ecss(
          {
            label: 'Message',
          },
          variants(options)
        )
      ),
    };
  },
});

const MessageWrapper = createComponent({
  as: 'div',
  useHook: useMessage,
  memo: true,
});

export type MessageOptions = {
  kind: ResponsiveValue<'error' | 'success' | 'warning' | 'info'>;
} & StylesOptions;

/**
 * @beta
 */
export type MessageProps = MessageOptions & HTMLAttributes<HTMLElement>;

// eslint-disable-next-line react/function-component-definition
function MessageComponent(
  { children, kind, ...props }: MessageProps,
  ref: React.Ref<HTMLDivElement>
) {
  let Icon;
  if (kind === 'error') Icon = ErrorIcon;
  if (kind === 'warning') Icon = WarningIcon;
  if (kind === 'success') Icon = SuccessIcon;
  if (kind === 'info') Icon = InformationIcon;
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <MessageWrapper kind={kind} {...props} ref={ref}>
      <Box
        as={Icon as As}
        flexShrink={0}
        mt="sp-4"
        mb={['sp-8', 'sp-0']}
        mr={['sp-0', 'sp-16']}
      />
      <Stack space="sp-8" width={1}>
        {children}
      </Stack>
    </MessageWrapper>
  );
}

/**
 * @beta
 */
export const Message = forwardRefWithAs<MessageProps, 'div'>(MessageComponent);
